import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '../../components/Layout';
import { apiFetch } from '../../utils/api';
import { useI18n } from '../../hooks/useI18n';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCwIcon } from 'lucide-react';

interface CostCenter {
  id: string;
  name: string;
  internal_id?: string;
  is_active: boolean;
}

interface PaymentMonthData {
  year: number;
  month: number;
  month_name: string;
  total_amount: number;
  payment_count: number;
}

interface PayableWeekData {
  week_start: string;
  week_end: string;
  total_amount: number;
  invoice_count: number;
}

interface CostCategoryMonthData {
  year: number;
  month: number;
  month_name: string;
  category_id?: string;
  category_name: string;
  total_amount: number;
  line_count: number;
}

interface DashboardData {
  cost_center_id?: string;
  months_back: number;
  weeks_ahead: number;
  base_currency: string;
  payments_over_months: PaymentMonthData[];
  upcoming_payables: PayableWeekData[];
  costs_by_category: CostCategoryMonthData[];
  cost_centers: CostCenter[];
  generated_at: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF7C7C'];

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCostCenter, setSelectedCostCenter] = useState<string>('all');
  const [monthsBack, setMonthsBack] = useState(12);
  const [weeksAhead, setWeeksAhead] = useState(8);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        months_back: monthsBack.toString(),
        weeks_ahead: weeksAhead.toString(),
      });

      if (selectedCostCenter !== 'all') {
        params.append('cost_center_id', selectedCostCenter);
      }

      const response = await apiFetch(`/api/analytics/dashboard?${params}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch analytics data: ${response.statusText}`);
      }

      const data = await response.json();
      setDashboardData(data);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedCostCenter, monthsBack, weeksAhead]);

  const formatCurrency = (value: number, currency?: string) => {
    const currencyCode = currency || dashboardData?.base_currency || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPaymentsData = (data: PaymentMonthData[]) => {
    return data.map(item => ({
      name: `${item.month_name} ${item.year}`,
      amount: item.total_amount,
      count: item.payment_count,
    }));
  };

  const formatPayablesData = (data: PayableWeekData[]) => {
    return data.map(item => ({
      name: `${new Date(item.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
      amount: item.total_amount,
      count: item.invoice_count,
    }));
  };

  const formatCostsByCategoryData = (data: CostCategoryMonthData[]) => {
    // Group by month and create series for each category
    const monthlyData: { [key: string]: any } = {};
    const categories = new Set<string>();

    data.forEach(item => {
      const monthKey = `${item.month_name} ${item.year}`;
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { name: monthKey };
      }
      monthlyData[monthKey][item.category_name] = item.total_amount;
      categories.add(item.category_name);
    });

    return {
      data: Object.values(monthlyData),
      categories: Array.from(categories)
    };
  };

  const getCostCategoryPieData = (data: CostCategoryMonthData[]) => {
    const categoryTotals: { [key: string]: number } = {};

    data.forEach(item => {
      if (!categoryTotals[item.category_name]) {
        categoryTotals[item.category_name] = 0;
      }
      categoryTotals[item.category_name] += item.total_amount;
    });

    return Object.entries(categoryTotals)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8); // Top 8 categories
  };

  const getCostCategoryTableData = (data: CostCategoryMonthData[]) => {
    // Get all unique months and categories
    const months = new Set<string>();
    const categories = new Set<string>();
    const dataMap: { [key: string]: { [key: string]: number } } = {};

    data.forEach(item => {
      const monthKey = `${item.month_name} ${item.year}`;
      months.add(monthKey);
      categories.add(item.category_name);

      if (!dataMap[item.category_name]) {
        dataMap[item.category_name] = {};
      }
      dataMap[item.category_name][monthKey] = item.total_amount;
    });

    // Sort months chronologically
    const sortedMonths = Array.from(months).sort((a, b) => {
      const [monthA, yearA] = a.split(' ');
      const [monthB, yearB] = b.split(' ');
      const dateA = new Date(`${monthA} 1, ${yearA}`);
      const dateB = new Date(`${monthB} 1, ${yearB}`);
      return dateA.getTime() - dateB.getTime();
    });

    // Sort categories alphabetically
    const sortedCategories = Array.from(categories).sort();

    return {
      months: sortedMonths,
      categories: sortedCategories,
      data: dataMap
    };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading analytics...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-red-600">Error: {error}</div>
        </div>
      </Layout>
    );
  }

  if (!dashboardData) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">No data available</div>
        </div>
      </Layout>
    );
  }

  const paymentsChartData = formatPaymentsData(dashboardData.payments_over_months);
  const payablesChartData = formatPayablesData(dashboardData.upcoming_payables);
  const costsData = formatCostsByCategoryData(dashboardData.costs_by_category);
  const costsPieData = getCostCategoryPieData(dashboardData.costs_by_category);
  const costsTableData = getCostCategoryTableData(dashboardData.costs_by_category);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
            <p className="text-muted-foreground">
              Business insights and financial analytics
            </p>
          </div>
                  {/*<Button onClick={fetchDashboardData} variant="outline" size="sm">
            <RefreshCwIcon className="h-4 w-4 mr-2" />
            Refresh
          </Button>*/}
        </div>

        {/* Filters */}
          <div className="flex gap-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select cost center" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cost Centers</SelectItem>
                    {dashboardData.cost_centers.map((cc) => (
                      <SelectItem key={cc.id} value={cc.id}>
                        {cc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Select value={monthsBack.toString()} onValueChange={(value) => setMonthsBack(parseInt(value))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                    <SelectItem value="18">18 months</SelectItem>
                    <SelectItem value="24">24 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Select value={weeksAhead.toString()} onValueChange={(value) => setWeeksAhead(parseInt(value))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4">4 weeks ahead</SelectItem>
                    <SelectItem value="8">8 weeks ahead</SelectItem>
                    <SelectItem value="12">12 weeks ahead</SelectItem>
                    <SelectItem value="16">16 weeks ahead</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Total Payments Over Months */}
          <Card >
            <CardHeader>
              <CardTitle>Total Payments Over Time</CardTitle>
              <CardDescription>
                Monthly payment totals for the last {monthsBack} months
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={paymentsChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(value) => formatCurrency(value, dashboardData?.base_currency)} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value) => [formatCurrency(Number(value), dashboardData?.base_currency), 'Amount']}
                      wrapperStyle={{ zIndex: 1000 }}
                    />
                    <Legend />
                    <Bar
                      dataKey="amount"
                      fill="#8884d8"
                      name="Payment Amount"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Payables by Week */}
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Payables</CardTitle>
              <CardDescription>
                Weekly payable amounts for the next {weeksAhead} weeks
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={payablesChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(value) => formatCurrency(value, dashboardData?.base_currency)} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value) => [formatCurrency(Number(value), dashboardData?.base_currency), 'Amount']}
                      wrapperStyle={{ zIndex: 1000 }}
                    />
                    <Legend />
                    <Bar dataKey="amount" fill="#82ca9d" name="Payable Amount" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Costs by Category - Full Width */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stacked Bar Chart */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Costs by Category Over Time</CardTitle>
              <CardDescription>
                Monthly costs broken down by category
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={costsData.data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(value) => formatCurrency(value, dashboardData?.base_currency)} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value, name) => [formatCurrency(Number(value), dashboardData?.base_currency), name]}
                      wrapperStyle={{ zIndex: 1000 }}
                    />
                    <Legend />
                    {costsData.categories.map((category, index) => (
                      <Bar
                        key={category}
                        dataKey={category}
                        stackId="costs"
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Top Cost Categories</CardTitle>
              <CardDescription>
                Total costs by category (top 8)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={costsPieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => {
                        const total = costsPieData.reduce((sum, entry) => sum + entry.value, 0);
                        const percent = total > 0 ? (((Number(value) || 0) / total) * 100).toFixed(0) : 0;
                        return `${name} ${percent}%`;
                      }}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {costsPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [formatCurrency(Number(value), dashboardData?.base_currency), 'Amount']}
                      wrapperStyle={{ zIndex: 1000 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Costs by Category Data Table */}
        <Card>
          <CardHeader>
            <CardTitle>Costs by Category - Detailed View</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-background font-semibold">Category</TableHead>
                    {costsTableData.months.map((month) => (
                      <TableHead key={month} className="text-right min-w-[120px]">
                        {month}
                      </TableHead>
                    ))}
                    <TableHead className="text-right font-semibold min-w-[120px]">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costsTableData.categories.map((category) => {
                    const categoryTotal = costsTableData.months.reduce((sum, month) => {
                      return sum + (costsTableData.data[category]?.[month] || 0);
                    }, 0);

                    return (
                      <TableRow key={category}>
                        <TableCell className="sticky left-0 bg-background font-medium">
                          {category}
                        </TableCell>
                        {costsTableData.months.map((month) => {
                          const amount = costsTableData.data[category]?.[month] || 0;
                          return (
                            <TableCell key={month} className="text-right">
                              {amount > 0 ? formatCurrency(amount, dashboardData?.base_currency) : '-'}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(categoryTotal, dashboardData?.base_currency)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Total row */}
                  <TableRow className="border-t-2 font-semibold">
                    <TableCell className="sticky left-0 bg-background">Total</TableCell>
                    {costsTableData.months.map((month) => {
                      const monthTotal = costsTableData.categories.reduce((sum, category) => {
                        return sum + (costsTableData.data[category]?.[month] || 0);
                      }, 0);
                      return (
                        <TableCell key={month} className="text-right">
                          {monthTotal > 0 ? formatCurrency(monthTotal, dashboardData?.base_currency) : '-'}
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-right">
                      {formatCurrency(
                        costsTableData.categories.reduce((sum, category) => {
                          return sum + costsTableData.months.reduce((monthSum, month) => {
                            return monthSum + (costsTableData.data[category]?.[month] || 0);
                          }, 0);
                        }, 0),
                        dashboardData?.base_currency
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export const Route = createFileRoute('/analytics')({
  component: AnalyticsPage,
})
